import {
  HighwayArea,
  Areas,
  Obstacle,
  LogicalSpace,
  ManualArea,
  RackingArea,
  SingularStationArea,
  StationaryArea,
} from '@thive/fddm-types';

import { assertNever } from '@/helpers/utils';
import { unique } from '@/modules/common/helpers/array';
import { Area } from '@modules/floorplanDesign/types';
import { FloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { getAngleBetweenSegments } from '@/modules/floorplanDesign/helpers/helpers';

import { Collision } from '@/modules/collision/types';
import { isHighwayArea } from '@/modules/floorplanDesign/helpers/typeguards';

/**
 * @description retrieves all the area ids that are somehow referenced by the area with the provided id its cutOutIds, be an area directly, or a highway through a segment indirectly
 * NOTE: cutOutIds mix area, segment, obstacle and logical space ids, so a lookup that finds nothing is expected here (hence the silent lookups)
 */
export const getAreaItsDeeplyReferencedCutOutAreaIds = (
  areaId: string,
  resolvedReferences: Set<string>,
  referencedAreaIds: string[],
  getAreaById: FloorplanDesignStore['getAreaById'],
  getHighwayBySegmentId: FloorplanDesignStore['getHighwayBySegmentIdSilently'],
): void => {
  const area = getAreaById(areaId);

  if (!area) return;

  switch (area.areaType) {
    case 'STATIONARY':
    case 'SINGULAR':
    case 'HIGHWAY':
    case 'RACKING': {
      const cutOutReferenceAreaAndSegmentIds = area.cutOutIds;

      cutOutReferenceAreaAndSegmentIds
        .filter((cutOutId) => !resolvedReferences.has(cutOutId))
        .forEach((cutOutId) => {
          // try to find an non-highway area based on cutOutId
          let areaReferencedByCutOutId = getAreaById(cutOutId);

          // if no area could be found based on cutOutId, it might be a segment id
          if (!areaReferencedByCutOutId) {
            // maybe cutOutId is a segment id, try to find a highway with segment id equal to a the cutOutId
            areaReferencedByCutOutId = getHighwayBySegmentId(cutOutId);

            // if still not found, something went wrong
            if (!areaReferencedByCutOutId) {
              resolvedReferences.add(cutOutId);
              return;
            }

            // highway already handled
            if (resolvedReferences.has(areaReferencedByCutOutId.id)) return;
          }

          resolvedReferences.add(cutOutId).add(areaReferencedByCutOutId.id);
          referencedAreaIds.push(areaReferencedByCutOutId.id);
          getAreaItsDeeplyReferencedCutOutAreaIds(
            areaReferencedByCutOutId.id,
            resolvedReferences,
            referencedAreaIds,
            getAreaById,
            getHighwayBySegmentId,
          );
        });

      break;
    }
    case 'MANUAL':
      resolvedReferences.add(areaId);
      // NOTE: this area does not have a cutOutIds property
      break;
    default:
      assertNever(area);
  }
};

export const getLogicalSpacesReferencedByCutOutIds = (
  area: Area,
  getLogicalSpaceById: FloorplanDesignStore['getLogicalSpaceById'],
): LogicalSpace[] => {
  switch (area.areaType) {
    case 'STATIONARY':
    case 'SINGULAR':
    case 'HIGHWAY':
    case 'RACKING': {
      return area.cutOutIds.flatMap((cutOutId) => {
        const logicalSpace = getLogicalSpaceById(cutOutId);
        if (!logicalSpace) return [];

        return logicalSpace;
      });
    }
    case 'MANUAL':
      // NOTE: this area does not have a cutOutIds property
      return [];
    default:
      assertNever(area);
  }
};

export const getObstaclesReferencedByCutOutIds = (
  area: Area,
  getObstacleById: FloorplanDesignStore['getObstacleByIdSilently'],
): Obstacle[] => {
  switch (area.areaType) {
    case 'STATIONARY':
    case 'SINGULAR':
    case 'HIGHWAY':
    case 'RACKING': {
      return area.cutOutIds.flatMap((cutOutId) => {
        const obstacle = getObstacleById(cutOutId);
        if (!obstacle) return [];

        return obstacle;
      });
    }
    case 'MANUAL':
      // NOTE: this area does not have a cutOutIds property
      return [];
    default:
      assertNever(area);
  }
};

export const buildAreaWithCutOutIds = <T extends Area>(area: T, cutOutIds: string[]): T => {
  switch (area.areaType) {
    case 'STATIONARY':
    case 'SINGULAR':
    case 'HIGHWAY':
    case 'RACKING': {
      return {
        ...area,
        cutOutIds: unique(cutOutIds),
      };
    }
    case 'MANUAL':
      // NOTE: this area does not have cutOutIds
      return area;
    default:
      assertNever(area);
  }
};

export const cutoutBetweenHighwaysCheck = (
  highwayA: HighwayArea,
  highwayB: HighwayArea,
  collision: Collision,
): boolean => {
  const segmentA = highwayA.segments.find((s) => s.id === collision.idA);
  const segmentB = highwayB.segments.find((s) => s.id === collision.idB);
  if (!segmentA || !segmentB) return false;

  const angle = getAngleBetweenSegments(segmentA, segmentB);
  if (angle < Math.PI / 6) return false;

  return true;
};

export const getCutouts = (
  areas: Areas,
  getHighwayBySegmentId: FloorplanDesignStore['getHighwayBySegmentId'],
): string[] => {
  const cutoutHighwayIds: string[] = [];
  const currentCutOutIds = areas.highwayAreas.flatMap((area) => area.cutOutIds);
  currentCutOutIds.forEach((cutOutId) => {
    const highway = getHighwayBySegmentId(cutOutId);
    if (highway) cutoutHighwayIds.push(highway.id);
  });
  return unique(cutoutHighwayIds);
};

const getResolvedHighwayBySegmentId = (
  segmentId: string,
  updatedAreas: Areas,
  getAreaByAreaIdOrSegmentId: FloorplanDesignStore['getAreaByAreaIdOrSegmentId'],
): HighwayArea | undefined => {
  const updatedHighway = updatedAreas.highwayAreas.find((h) =>
    h.segments.some((s) => s.id === segmentId),
  );
  if (updatedHighway) return updatedHighway;

  const existingArea = getAreaByAreaIdOrSegmentId(segmentId);
  if (existingArea && isHighwayArea(existingArea)) {
    return existingArea;
  }

  return undefined;
};

export const getPersistedOverlapAction = (
  collision: Collision,
  fromArea: Area,
  fromIds: string[],
  updatedAreas: Areas,
  getAreaByAreaIdOrSegmentId: FloorplanDesignStore['getAreaByAreaIdOrSegmentId'],
): { otherId: string; action: 'add' | 'remove' } | undefined => {
  const otherId = fromIds.includes(collision.idA) ? collision.idB : collision.idA;

  if (!isHighwayArea(fromArea)) return undefined;

  const otherHighway = getResolvedHighwayBySegmentId(
    otherId,
    updatedAreas,
    getAreaByAreaIdOrSegmentId,
  );
  if (!otherHighway || otherHighway.id === fromArea.id) {
    return undefined;
  }

  return {
    otherId,
    action: cutoutBetweenHighwaysCheck(fromArea, otherHighway, collision) ? 'add' : 'remove',
  };
};

export const getPersistedOverlaps = (
  fromIds: string[],
  collisionState: Map<string, string[]>,
  removedCollisions: Collision[],
  newCollisions: Collision[],
): Collision[] => {
  return fromIds.flatMap((fromId) => {
    return (collisionState.get(fromId) ?? [])
      .map((toId) => ({
        idA: fromId,
        idB: toId,
      }))
      .filter(
        (collision) =>
          !removedCollisions.some(
            (rc) =>
              (rc.idA === collision.idA && rc.idB === collision.idB) ||
              (rc.idA === collision.idB && rc.idB === collision.idA),
          ) &&
          !newCollisions.some(
            (nc) =>
              (nc.idA === collision.idA && nc.idB === collision.idB) ||
              (nc.idA === collision.idB && nc.idB === collision.idA),
          ),
      );
  });
};

export const getCutoutsFromCollisions = (
  fromArea: Area,
  fromIds: string[],
  collisions: Collision[],
  areaToUpdateMap: Map<
    string,
    HighwayArea | ManualArea | RackingArea | SingularStationArea | StationaryArea
  >,
  updatedAreas: Areas,
  getAreaByAreaIdOrSegmentId: FloorplanDesignStore['getAreaByAreaIdOrSegmentId'],
  getObstacleById: FloorplanDesignStore['getObstacleByIdSilently'],
  getLogicalSpaceById: FloorplanDesignStore['getLogicalSpaceById'],
): string[] => {
  return collisions.flatMap((collision) => {
    const otherId = fromIds.includes(collision.idA) ? collision.idB : collision.idA;

    const updatedArea = areaToUpdateMap.get(otherId);
    if (updatedArea && updatedArea.id !== fromArea.id) {
      if (isHighwayArea(fromArea) && isHighwayArea(updatedArea)) {
        if (cutoutBetweenHighwaysCheck(fromArea, updatedArea, collision)) {
          return otherId;
        }
      }
      return [];
    }
    const updatedHighway = updatedAreas.highwayAreas.find((h) =>
      h.segments.some((s) => s.id === otherId),
    );
    if (updatedHighway && updatedHighway.id !== fromArea.id) {
      if (isHighwayArea(fromArea)) {
        if (cutoutBetweenHighwaysCheck(updatedHighway, fromArea, collision)) {
          return otherId;
        }
      }
      return [];
    }
    const area = getAreaByAreaIdOrSegmentId(otherId);
    if (area && area.id !== fromArea.id) {
      if (isHighwayArea(fromArea) && isHighwayArea(area)) {
        if (cutoutBetweenHighwaysCheck(fromArea, area, collision)) {
          return otherId;
        }
      }
      return [];
    }

    const obstacle = getObstacleById(otherId);
    if (obstacle) return otherId;
    const logicalSpace = getLogicalSpaceById(otherId);
    if (logicalSpace) return otherId;

    return [];
  });
};
